import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { formatRelativeDue } from '../../lib/templateDueDate';
import { mergeTeamOptions } from '../../lib/teams';
import type { TaskTemplateItem } from '../../types/taskTemplate';
import type { ItemCreatePayload, ItemUpdatePayload } from './api';

// Re-exported for existing settings-internal imports; the implementation
// lives in lib/templateDueDate (shared with the apply-template preview).
export { formatRelativeDue };

// §3.6 Edit Task Item modal. Used for both add + edit. The relative due
// date is authored as "no due date" (null) OR a non-negative day count +
// a before/after direction; stored as a signed relativeDueDays
// (negative = before launch, positive = after, 0 = on launch day).
function splitRelative(days: number | null): {
  hasDue: boolean;
  magnitude: string;
  direction: 'before' | 'after';
} {
  if (days === null) {
    return { hasDue: false, magnitude: '0', direction: 'before' };
  }
  if (days < 0) {
    return { hasDue: true, magnitude: String(-days), direction: 'before' };
  }
  return { hasDue: true, magnitude: String(days), direction: 'after' };
}

export function TaskTemplateItemForm({
  item,
  teams,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  item: TaskTemplateItem | null; // null = create
  teams: string[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (payload: ItemCreatePayload | ItemUpdatePayload) => void;
}) {
  const initial = splitRelative(item?.relativeDueDays ?? null);
  // Item 0: locked dropdown sourced from distinct User.team values (the
  // `teams` prop, already distinctTeams(users) from the caller). An orphan
  // defaultTeam — a value no active user's team matches — stays selectable
  // via mergeTeamOptions so editing an old template doesn't silently drop it.
  const teamOptions = mergeTeamOptions(teams, item?.defaultTeam ?? null);
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [hasDue, setHasDue] = useState(initial.hasDue);
  const [magnitude, setMagnitude] = useState(initial.magnitude);
  const [direction, setDirection] = useState<'before' | 'after'>(
    initial.direction,
  );
  const [defaultTeam, setDefaultTeam] = useState(item?.defaultTeam ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const resolveRelative = (): number | null => {
    if (!hasDue) return null;
    const n = Number(magnitude);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error('Enter a whole, non-negative number of days.');
    }
    return direction === 'before' ? -n : n;
  };

  const submit = () => {
    if (!title.trim()) {
      setLocalError('Title is required.');
      return;
    }
    let relativeDueDays: number | null;
    try {
      relativeDueDays = resolveRelative();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : String(e));
      return;
    }
    setLocalError(null);
    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      relativeDueDays,
      defaultTeam: defaultTeam.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">
          {item ? 'Edit task item' : 'Add task item'}
        </h3>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Title
            </span>
            <input
              type="text"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Description
            </span>
            <textarea
              value={description}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>

          <div>
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Due date (relative to launch)
            </span>
            <label className="mb-2 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!hasDue}
                onChange={(e) => setHasDue(!e.target.checked)}
                className="h-4 w-4"
              />
              No due date
            </label>
            {hasDue && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={magnitude}
                  onChange={(e) => setMagnitude(e.target.value)}
                  className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                />
                <span className="text-sm text-slate-600">days</span>
                <SelectField
                  size="sm"
                  aria-label="Offset direction"
                  value={direction}
                  onValueChange={(v) => setDirection(v as 'before' | 'after')}
                  options={[
                    { value: 'before', label: 'before' },
                    { value: 'after', label: 'after' },
                  ]}
                />
                <span className="text-sm text-slate-600">launch</span>
              </div>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Default team (optional)
            </span>
            <SelectField
              aria-label="Default team"
              value={defaultTeam}
              onValueChange={setDefaultTeam}
              options={[
                { value: '', label: '— No default team —' },
                ...teamOptions.map((t) => ({ value: t, label: t })),
              ]}
            />
            <span className="mt-1 block text-[11px] text-slate-400">
              Teams come from existing team values on users. Applied tasks
              default to this team (assignable to a person later).
            </span>
          </label>

          {(localError || error) && (
            <p className="text-sm text-red-600">{localError ?? error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
